/**
 * Visual chat notifications — independent of audio.
 *
 * Browsers (Brave especially) routinely block or throttle audio in
 * background tabs, so the audible "pling" can never be the only signal.
 * This module guarantees a visible signal for every incoming chat message:
 *
 * - Tab-title unread counter: "(2) Acolori PM" while the tab is hidden,
 *   cleared automatically when the user returns.
 * - Browser system notifications (Notification API) when the tab is hidden
 *   and the user has granted permission. Permission is requested once, on
 *   the first user gesture (same pattern as the audio unlock).
 *
 * No external dependencies, no timers except event listeners.
 */

const MAX_BODY_LENGTH = 120;

class ChatNotify {
  private baseTitle: string = typeof document !== 'undefined' ? document.title : 'Acolori PM';
  private hiddenUnread = 0;
  private listenersAttached = false;
  private permissionListenerAttached = false;

  /** Wire up visibility listeners + one-time permission request. Idempotent. */
  init(): void {
    if (typeof document === 'undefined' || this.listenersAttached) return;
    this.listenersAttached = true;

    // Capture the real title once (index.html sets it; SPA never changes it).
    this.baseTitle = document.title || 'Acolori PM';

    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) this.clearUnread();
    });
    window.addEventListener('focus', () => this.clearUnread());

    this.attachPermissionListener();
  }

  /**
   * Ask for notification permission once, on the first user gesture after
   * load — never as a page-load popup. No-op when already granted/denied
   * or unsupported.
   */
  private attachPermissionListener(): void {
    if (this.permissionListenerAttached) return;
    if (typeof window === 'undefined' || !('Notification' in window)) return;
    if (Notification.permission !== 'default') return;
    this.permissionListenerAttached = true;

    const request = () => {
      document.removeEventListener('click', request);
      document.removeEventListener('keydown', request);
      try {
        void Notification.requestPermission();
      } catch {
        // Older browsers use the callback form; ignore — best effort.
        try { Notification.requestPermission(() => {}); } catch {}
      }
    };
    document.addEventListener('click', request, { once: true, passive: true });
    document.addEventListener('keydown', request, { once: true, passive: true });
  }

  /**
   * Report an incoming chat message from another user. Fires the visible
   * channels when the tab is hidden; cheap no-op when the tab is visible
   * (the in-app badges cover that case).
   */
  messageReceived(opts: { authorName?: string; channelLabel?: string; body?: string }): void {
    if (typeof document === 'undefined' || !document.hidden) return;

    this.hiddenUnread++;
    this.updateTitle();
    this.showSystemNotification(opts);
  }

  private updateTitle(): void {
    document.title = this.hiddenUnread > 0
      ? `(${this.hiddenUnread > 99 ? '99+' : this.hiddenUnread}) ${this.baseTitle}`
      : this.baseTitle;
  }

  private clearUnread(): void {
    if (this.hiddenUnread === 0) return;
    this.hiddenUnread = 0;
    document.title = this.baseTitle;
  }

  private showSystemNotification(opts: { authorName?: string; channelLabel?: string; body?: string }): void {
    if (!('Notification' in window) || Notification.permission !== 'granted') return;

    const title = opts.authorName
      ? (opts.channelLabel ? `${opts.authorName} · ${opts.channelLabel}` : opts.authorName)
      : 'Neue Nachricht';
    let body = (opts.body ?? '').replace(/\s+/g, ' ').trim();
    if (body.length > MAX_BODY_LENGTH) body = body.slice(0, MAX_BODY_LENGTH - 1) + '…';

    try {
      const n = new Notification(title, {
        body: body || 'Neue Chat-Nachricht',
        icon: '/acolori-logo.png',
        // One notification per app: a newer message replaces the older one
        // instead of stacking, keeping the signal unobtrusive.
        tag: 'acolori-chat',
        silent: true, // sound is handled (and user-configured) in-app
      });
      n.onclick = () => {
        try { window.focus(); } catch {}
        n.close();
      };
    } catch {
      // Notification constructor can throw (e.g. some Android browsers) —
      // the title counter above already covers the visible signal.
    }
  }
}

export const chatNotify = new ChatNotify();
